import {
  REASSIGNMENT_REASON_LABELS,
  type ReassignmentDetail,
  type ReassignmentStatus,
} from '@cartwheel/api/v1'
import React, { type ReactNode } from 'react'
import { Announcement, AnnouncementType } from './announcement'
import { Button } from './button'
import { DateInput } from './date-input'
import { DetailRow } from './detail-row'
import { Modal } from './modal'
import { RadioGroup } from './radio-group'
import { Spinner } from './spinner'
import { Textarea } from './textarea'
import { formatEffectiveDate } from './utilities/format-effective-date'

type ReassignmentAction = Exclude<ReassignmentStatus, 'initiated'>
type ReasonKey = ReassignmentDetail['reasons'][number]

export interface ReassignmentEditValues {
  effective_date: string
  reason: ReasonKey | ''
  notes: string
}

function buildReasonOptions(): Array<{ value: ReasonKey; label: string }> {
  return (Object.keys(REASSIGNMENT_REASON_LABELS) as ReasonKey[]).map((value) => ({
    value,
    label: REASSIGNMENT_REASON_LABELS[value] || value,
  }))
}

/**
 * Format reassignment reasons using human-readable labels
 * @param reasons Array of reason keys
 * @returns Comma-separated string of formatted reason labels
 */
function formatReasons(reasons: ReasonKey[]): string {
  return reasons.map((r) => REASSIGNMENT_REASON_LABELS[r] || r).join(', ')
}

interface ReassignmentDetailModalProps {
  // Modal control
  isOpen: boolean
  onClose: () => void

  // Data states
  isLoading?: boolean
  error?: string | null
  data?: ReassignmentDetail | null

  // Review controls
  canReview?: boolean

  // Edit controls (only effective while status === 'initiated')
  canEdit?: boolean
  isEditing?: boolean
  onStartEdit?: () => void
  onCancelEdit?: () => void
  editValues?: ReassignmentEditValues
  onEditValuesChange?: (values: ReassignmentEditValues) => void
  onSaveEdit?: () => void
  isSaving?: boolean
  editError?: string | null

  // Action form state (when canReview && status === 'initiated')
  selectedAction?: ReassignmentAction | ''
  onActionChange?: (action: ReassignmentAction) => void
  additionalDetails?: string
  onAdditionalDetailsChange?: (details: string) => void

  // Submission
  onSubmit?: () => void
  isPending?: boolean
  submitError?: string | null

  // Slots for domain-specific content
  renderClinicianHeader?: (clinician: ReassignmentDetail['clinician']) => ReactNode
  renderTypeDisplay?: (reassignment: ReassignmentDetail) => ReactNode
  renderWarnings?: (
    reassignment: ReassignmentDetail,
    selectedAction: ReassignmentAction | ''
  ) => ReactNode

  // Action options
  actionOptions?: Array<{ value: string; label: string }>
}

export function ReassignmentDetailModal({
  isOpen,
  onClose,
  isLoading = false,
  error = null,
  data = null,
  canReview = false,
  canEdit = false,
  isEditing = false,
  onStartEdit,
  onCancelEdit,
  editValues,
  onEditValuesChange,
  onSaveEdit,
  isSaving = false,
  editError = null,
  selectedAction = '',
  onActionChange,
  additionalDetails = '',
  onAdditionalDetailsChange,
  onSubmit,
  isPending = false,
  submitError = null,
  renderClinicianHeader,
  renderTypeDisplay,
  renderWarnings,
  actionOptions = [],
}: ReassignmentDetailModalProps) {
  const detailsId = React.useId()
  const editingEffective =
    canEdit && data?.status === 'initiated' && isEditing && editValues && onEditValuesChange
  const todayIso = new Date().toISOString().split('T')[0]

  const handleClose = () => {
    onClose()
  }

  const updateEditValue = <K extends keyof ReassignmentEditValues>(
    key: K,
    value: ReassignmentEditValues[K]
  ) => {
    if (!editValues || !onEditValuesChange) return
    onEditValuesChange({ ...editValues, [key]: value })
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reassignment Request"
      aria-describedby="reassignment-details"
    >
      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {error && <Announcement type={AnnouncementType.Error} message={error} />}

      {data && (
        <div id={detailsId} className="space-y-6">
          {/* Clinician Header */}
          {renderClinicianHeader?.(data.clinician)}

          {/* Warnings */}
          {renderWarnings?.(data, selectedAction)}

          {/* Details Grid */}
          <div className="space-y-4">
            {renderTypeDisplay && (
              <DetailRow label="Reassignment Type" value={renderTypeDisplay(data)} />
            )}

            <DetailRow
              label={data.reassignment_type === 'single_client' ? 'Service(s)' : 'Services'}
              value={data.journey_info?.service_name || data.journey_info?.services || 'N/A'}
            />

            <DetailRow
              label="Did the family request this reassignment?"
              value={data.family_initiated ? 'Yes' : 'No'}
            />

            <DetailRow label="Initiated by" value={data.submitted_by.name} />

            {editingEffective ? (
              <DetailRow
                label="Reason for Reassignment"
                value={
                  <RadioGroup
                    name="edit-reason"
                    value={editValues.reason}
                    onChange={(value) => updateEditValue('reason', value as ReasonKey)}
                    options={buildReasonOptions()}
                    direction="vertical"
                  />
                }
              />
            ) : (
              <DetailRow
                label="Reason for Reassignment"
                value={data.reasons.length > 0 ? formatReasons(data.reasons) : 'No reason provided'}
              />
            )}

            {editingEffective ? (
              <DetailRow
                label="Notes / Updates"
                value={
                  <Textarea
                    value={editValues.notes}
                    onChange={(e) => updateEditValue('notes', e.target.value)}
                    placeholder="Input text"
                    rows={3}
                  />
                }
              />
            ) : (
              data.notes && <DetailRow label="Notes / Updates" value={data.notes} />
            )}

            {data.student_availability && (
              <DetailRow label="Client's Availability" value={data.student_availability} />
            )}

            {editingEffective ? (
              <DetailRow
                label="Effective date"
                value={
                  <DateInput
                    name="edit-effective-date"
                    value={editValues.effective_date}
                    onChange={(e) => updateEditValue('effective_date', e.target.value)}
                    min={todayIso}
                  />
                }
              />
            ) : (
              data.effective_date && (
                <DetailRow
                  label="Effective date"
                  value={formatEffectiveDate(data.effective_date)}
                />
              )
            )}
          </div>

          {/* Action Selection (only for reviewers and initiated status) */}
          {!isEditing &&
            canReview &&
            data.status === 'initiated' &&
            actionOptions.length > 0 &&
            onActionChange &&
            onAdditionalDetailsChange && (
              <div className="pt-4 border-t border-gray-stroke space-y-4">
                <div>
                  <div className="text-charcoal font-semibold mb-2">Select an action</div>
                  <RadioGroup
                    name="action"
                    options={actionOptions}
                    value={selectedAction}
                    onChange={(value) => onActionChange(value as ReassignmentAction)}
                    direction="vertical"
                  />
                </div>

                <div>
                  <div className="text-charcoal font-semibold mb-2">
                    {selectedAction === 'denied'
                      ? 'Denial reason (required)'
                      : 'Additional details'}
                  </div>
                  <Textarea
                    value={additionalDetails}
                    onChange={(e) => onAdditionalDetailsChange(e.target.value)}
                    placeholder="Input text"
                    rows={3}
                    required={selectedAction === 'denied'}
                  />
                </div>
              </div>
            )}

          {/* Error Message */}
          {submitError && <Announcement type={AnnouncementType.Error} message={submitError} />}
          {editError && <Announcement type={AnnouncementType.Error} message={editError} />}

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-stroke">
            {isEditing ? (
              <>
                <Button variant="secondary" onClick={onCancelEdit} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={onSaveEdit}
                  disabled={isSaving || !editValues?.effective_date || !editValues?.reason}
                >
                  {isSaving ? 'Saving...' : 'Save changes'}
                </Button>
              </>
            ) : (
              <>
                {canEdit && data.status === 'initiated' && onStartEdit && (
                  <Button variant="secondary" onClick={onStartEdit}>
                    Edit
                  </Button>
                )}
                {canReview && data.status === 'initiated' && onSubmit && (
                  <Button
                    variant="primary"
                    disabled={
                      !selectedAction ||
                      isPending ||
                      (selectedAction === 'denied' && !additionalDetails.trim())
                    }
                    onClick={onSubmit}
                  >
                    {isPending ? 'Submitting...' : 'Submit'}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
